import { faSend, faExpand } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, TextInput, Modal, ModalVariant } from '@patternfly/react-core';
import React, { useState, useEffect, useRef } from 'react';
import { ChatActions } from '../../api/lambda/ChatActions';
import { ChatMessage } from '../../api/types';
import { useApplication } from '../user/ApplicationProvider';
import { getExtendedPrompt } from '../../api/zone/getExtendedPrompt';

export interface Message {
	id: number;
	text: string;
	isUser: boolean;
	sender: 'user' | 'bot';
}

interface ChatInterfaceProps {
	context?: string;
	contextId?: string | number; // When this changes, reset the chat
	/** Render messages externally in a shared thread instead of inside ChatInterface */
	externalThread?: boolean;
	/** Called when messages change, so parent can render them in a shared thread */
	onMessagesChange?: (messages: Message[]) => void;
	/** Called when isThinking changes */
	onThinkingChange?: (isThinking: boolean) => void;
	/** Whether the modal is managed externally */
	showModal?: boolean;
	onModalClose?: () => void;
	/** Extra content to render before messages in the modal (e.g., insights card) */
	modalHeaderContent?: React.ReactNode;
	/** Disable input until context is ready */
	disabled?: boolean;
	/** Called once when the user first types into the input */
	onFirstInteraction?: () => void;
	/** Auto-submit this message on mount (used by Discover "Chat about this") */
	autoSubmitMessage?: string;
}

// Simple markdown renderer for chat messages
// TODO: We will likely want to replace this with a full library later
const renderMarkdown = (text: string): React.ReactNode => {
	const paragraphs = text.split(/\n\n+/);

	return paragraphs.map((paragraph, pIndex) => {
		const lines = paragraph.split('\n');
		const isBulletList = lines.every((line) => /^\s*[-*]\s/.test(line) || line.trim() === '');
		const isNumberedList = lines.every(
			(line) => /^\s*\d+\.\s/.test(line) || line.trim() === ''
		);

		if (isBulletList && lines.some((line) => line.trim())) {
			return (
				<ul
					key={pIndex}
					style={{ margin: '0.5em 0', paddingLeft: '1.5em' }}
				>
					{lines
						.filter((line) => line.trim())
						.map((line, lIndex) => (
							<li key={lIndex}>
								{renderInlineMarkdown(line.replace(/^\s*[-*]\s/, ''))}
							</li>
						))}
				</ul>
			);
		}

		if (isNumberedList && lines.some((line) => line.trim())) {
			return (
				<ol
					key={pIndex}
					style={{ margin: '0.5em 0', paddingLeft: '1.5em' }}
				>
					{lines
						.filter((line) => line.trim())
						.map((line, lIndex) => (
							<li key={lIndex}>
								{renderInlineMarkdown(line.replace(/^\s*\d+\.\s/, ''))}
							</li>
						))}
				</ol>
			);
		}

		// Regular paragraph with line breaks
		return (
			<p
				key={pIndex}
				style={{ margin: '0.5em 0' }}
			>
				{lines.map((line, lIndex) => (
					<React.Fragment key={lIndex}>
						{renderInlineMarkdown(line)}
						{lIndex < lines.length - 1 && <br />}
					</React.Fragment>
				))}
			</p>
		);
	});
};

// Render inline markdown (bold, italic)
const renderInlineMarkdown = (text: string): React.ReactNode => {
	// Process bold and italic
	const parts: React.ReactNode[] = [];
	let remaining = text;
	let key = 0;

	while (remaining) {
		// Bold: **text** or __text__
		const boldMatch = remaining.match(/(\*\*|__)(.+?)\1/);
		// Italic: *text* or _text_ (but not inside bold)
		const italicMatch = remaining.match(/(\*|_)([^*_]+?)\1/);

		const match =
			boldMatch && (!italicMatch || boldMatch.index! <= italicMatch.index!)
				? boldMatch
				: italicMatch;

		if (match && match.index !== undefined) {
			if (match.index > 0) {
				parts.push(remaining.slice(0, match.index));
			}
			if (match[1] === '**' || match[1] === '__') {
				parts.push(<strong key={key++}>{match[2]}</strong>);
			} else {
				parts.push(<em key={key++}>{match[2]}</em>);
			}
			remaining = remaining.slice(match.index + match[0].length);
		} else {
			parts.push(remaining);
			break;
		}
	}

	return parts.length === 1 ? parts[0] : <>{parts}</>;
};

/** Renders chat messages (for use in external threads) */
export const ChatMessages: React.FC<{
	messages: Message[];
	isThinking: boolean;
}> = ({ messages, isThinking }) => (
	<>
		{messages.map((msg, i) => (
			<div
				key={i}
				className={`chat-bubble ${msg.sender === 'user' ? 'user' : 'bot'}`}
			>
				{msg.sender === 'bot' ? renderMarkdown(msg.text) : msg.text}
			</div>
		))}
		{isThinking && (
			<div className="chat-bubble bot">
				<div className="typing-indicator">
					<span className="dot"></span>
					<span className="dot"></span>
					<span className="dot"></span>
				</div>
			</div>
		)}
	</>
);

/** Renders the pinned chat input area */
export const ChatInput: React.FC<{
	input: string;
	setInput: (val: string) => void;
	onSend: () => void;
	isThinking: boolean;
	placeholder?: string;
	disabled?: boolean;
}> = ({ input, setInput, onSend, isThinking, placeholder, disabled }) => {
	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			onSend();
		}
	};

	return (
		<div className="chat-input-group">
			<TextInput
				placeholder={
					disabled ? 'Generating insights...' : placeholder ?? 'Type a message...'
				}
				value={input}
				onChange={(value) => setInput(value)}
				onKeyPress={handleKeyPress}
				isDisabled={isThinking || disabled}
				className="chat-text-input"
			/>
			<Button
				onClick={onSend}
				isDisabled={!input.trim() || isThinking || disabled}
				className="chat-send-btn"
				variant="primary"
			>
				<FontAwesomeIcon icon={faSend} />
			</Button>
		</div>
	);
};

const ChatInterface: React.FC<ChatInterfaceProps> = ({
	context = '',
	contextId,
	externalThread = false,
	onMessagesChange,
	onThinkingChange,
	showModal,
	onModalClose,
	modalHeaderContent,
	disabled,
	onFirstInteraction,
	autoSubmitMessage,
}) => {
	const { zoneSettings } = useApplication();
	const [messages, setMessages] = useState<Message[]>([]);
	const [input, setInput] = useState('');
	const hasInteractedRef = useRef(false);
	const hasAutoSubmittedRef = useRef(false);
	const [isThinking, setIsThinking] = useState(false);
	const [internalShowModal, setInternalShowModal] = useState(false);

	const messagesEndRef = useRef<HTMLDivElement | null>(null);
	const chatContainerRef = useRef<HTMLDivElement | null>(null);
	const modalChatContainerRef = useRef<HTMLDivElement | null>(null);

	const isModalOpen = showModal !== undefined ? showModal : internalShowModal;
	const handleModalClose = onModalClose ?? (() => setInternalShowModal(false));

	const scrollToBottom = () => {
		const container = isModalOpen ? modalChatContainerRef.current : chatContainerRef.current;
		if (container) {
			container.scrollTop = container.scrollHeight;
		}
	};

	// Reset chat when graph context changes
	useEffect(() => {
		setMessages([]);
		setInput('');
		hasAutoSubmittedRef.current = false;
	}, [contextId]);

	useEffect(() => {
		onMessagesChange?.(messages);
	}, [messages]);

	useEffect(() => {
		onThinkingChange?.(isThinking);
	}, [isThinking]);

	useEffect(() => {
		if (messages.length > 0 || isThinking) {
			scrollToBottom();
		}
	}, [messages, isThinking, isModalOpen]);

	const sendMessage = async (text: string) => {
		if (!text.trim() || isThinking) return;

		const userMessage: Message = {
			id: Date.now(),
			text,
			isUser: true,
			sender: 'user',
		};

		setMessages((prev) => [...prev, userMessage]);
		setIsThinking(true);

		try {
			// Build full conversation history for API (stateless)
			const apiMessages: ChatMessage[] = [
				...messages.map(
					(m): ChatMessage => ({
						role: m.sender === 'user' ? 'user' : 'assistant',
						content: m.text,
					})
				),
				{ role: 'user', content: text },
			];

			const response = await ChatActions.SendMessage({
				messages: apiMessages,
				context: context,
				metadata: { extended_prompt: getExtendedPrompt(zoneSettings) },
			});

			if (response.success && response.data?.response) {
				const aiMessage: Message = {
					id: Date.now() + 1,
					text: response.data.response.content,
					isUser: false,
					sender: 'bot',
				};
				setMessages((prev) => [...prev, aiMessage]);
			} else {
				const errorMessage: Message = {
					id: Date.now() + 1,
					text: 'Sorry, I encountered an error. Please try again.',
					isUser: false,
					sender: 'bot',
				};
				setMessages((prev) => [...prev, errorMessage]);
			}
		} catch (error) {
			const errorMessage: Message = {
				id: Date.now() + 1,
				text: 'Sorry, I encountered an error. Please try again.',
				isUser: false,
				sender: 'bot',
			};
			setMessages((prev) => [...prev, errorMessage]);
		} finally {
			setIsThinking(false);
		}
	};

	const handleSend = async () => {
		if (!input.trim() || isThinking) return;
		const text = input;
		setInput('');
		await sendMessage(text);
	};

	const doSend = () => void handleSend();

	// Auto-submit message from Discover page
	useEffect(() => {
		if (
			autoSubmitMessage &&
			!hasAutoSubmittedRef.current &&
			!disabled &&
			!isThinking &&
			messages.length === 0
		) {
			hasAutoSubmittedRef.current = true;
			void sendMessage(autoSubmitMessage);
		}
	}, [autoSubmitMessage, disabled, isThinking, messages.length]);

	const handleInputChange = (value: string) => {
		setInput(value);
		if (!hasInteractedRef.current && value.length > 0) {
			hasInteractedRef.current = true;
			onFirstInteraction?.();
		}
	};

	// External thread mode: only render the pinned input (messages rendered by parent)
	if (externalThread) {
		return (
			<>
				<ChatInput
					input={input}
					setInput={handleInputChange}
					onSend={doSend}
					isThinking={isThinking}
					disabled={disabled}
				/>
				<Modal
					variant={ModalVariant.large}
					title="AI * Insights"
					isOpen={isModalOpen}
					onClose={handleModalClose}
					className="chat-modal"
				>
					<div className="chat-container chat-container-modal">
						<div
							className="chat-messages chat-messages-modal"
							ref={modalChatContainerRef}
						>
							{modalHeaderContent}
							<ChatMessages
								messages={messages}
								isThinking={isThinking}
							/>
							<div ref={messagesEndRef} />
						</div>
						<ChatInput
							input={input}
							setInput={setInput}
							onSend={doSend}
							isThinking={isThinking}
						/>
					</div>
				</Modal>
			</>
		);
	}

	// Legacy inline mode (kept for backward compatibility)
	const chatContent = (isModal: boolean) => (
		<>
			<div
				className={`chat-messages ${isModal ? 'chat-messages-modal' : ''}`}
				ref={isModal ? modalChatContainerRef : chatContainerRef}
			>
				{messages.map((msg, i) => (
					<div
						key={i}
						className={`chat-bubble ${msg.sender === 'user' ? 'user' : 'bot'}`}
					>
						{msg.sender === 'bot' ? renderMarkdown(msg.text) : msg.text}
					</div>
				))}

				{isThinking && (
					<div className="chat-bubble bot">
						<div className="typing-indicator">
							<span className="dot"></span>
							<span className="dot"></span>
							<span className="dot"></span>
						</div>
					</div>
				)}

				<div ref={messagesEndRef} />
			</div>

			<div className="chat-input-group">
				<TextInput
					placeholder="Type your message..."
					value={input}
					onChange={(value) => setInput(value)}
					onKeyPress={(e: React.KeyboardEvent) => {
						if (e.key === 'Enter' && !e.shiftKey) {
							e.preventDefault();
							doSend();
						}
					}}
					isDisabled={isThinking}
					className="chat-text-input"
				/>
				<Button
					onClick={doSend}
					isDisabled={!input.trim() || isThinking}
					className="chat-send-btn"
					variant="primary"
				>
					<FontAwesomeIcon icon={faSend} />
				</Button>
			</div>
		</>
	);

	return (
		<>
			<div className="chat-container">
				<div className="chat-header">
					<small>
						<FontAwesomeIcon icon={faExpand} /> Follow up question? Prompt to get
						further insight...
					</small>
					<Button
						variant="plain"
						className="chat-expand-btn"
						onClick={() => setInternalShowModal(true)}
						title="Expand chat"
					>
						<FontAwesomeIcon icon={faExpand} />
					</Button>
				</div>
				{chatContent(false)}
			</div>

			<Modal
				variant={ModalVariant.large}
				title="AI Chat"
				isOpen={internalShowModal}
				onClose={() => setInternalShowModal(false)}
				className="chat-modal"
			>
				<div className="chat-container chat-container-modal">{chatContent(true)}</div>
			</Modal>
		</>
	);
};

export default ChatInterface;
